/**
 * ProductStatusBadge Component - Story 6.2: Create My Products Page
 * Displays product status with color-coded badge
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProductStatusBadgeProps } from '../types/products.types';
import { STATUS_CONFIG } from '../constants/statusConstants';

/**
 * ProductStatusBadge Component
 * Displays product status with icon and color coding
 *
 * @component
 * @example
 * <ProductStatusBadge status="ACTIVE" />
 * <ProductStatusBadge status="INACTIVE" className="text-sm" />
 */
export const ProductStatusBadge: React.FC<ProductStatusBadgeProps> = ({
  status,
  className,
}) => {
  const config = STATUS_CONFIG[status];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full', config.bgColor)}>
        {config.icon}
        <span className={cn('text-sm font-medium', config.textColor)}>{config.label}</span>
      </div>
    </div>
  );
};

/**
 * ProductStatusBadgeCompact Component
 * Compact version with just badge
 */
export const ProductStatusBadgeCompact: React.FC<ProductStatusBadgeProps> = ({
  status,
  className,
}) => {
  const config = STATUS_CONFIG[status];

  return (
    <Badge variant={config.variant} className={cn('gap-1.5', config.className, className)}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

/**
 * ProductStatusIndicator Component
 * Just the icon indicator
 */
export const ProductStatusIndicator: React.FC<ProductStatusBadgeProps> = ({
  status,
  className,
}) => {
  const config = STATUS_CONFIG[status];

  return (
    <div
      className={cn('flex items-center justify-center', className)}
      title={config.label}
      aria-label={`Status: ${config.label}`}
    >
      {config.icon}
    </div>
  );
};

export type { ProductStatusBadgeProps };
